import React from 'react';
import {
  BuildingOfficeIcon,
  UserIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  CalendarIcon,
  IdentificationIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { InvoiceData } from '../types/invoice';
import { format, parseISO } from 'date-fns';

interface InvoiceDataDisplayProps {
  data: InvoiceData;
  filename?: string;
  className?: string;
}

const InvoiceDataDisplay: React.FC<InvoiceDataDisplayProps> = ({
  data,
  filename,
  className = '',
}) => {
  const formatCurrency = (amount?: number, currency?: string) => {
    if (amount === undefined || amount === null) return 'N/A';
    const currencySymbol = currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '$';
    return `${currencySymbol}${amount.toFixed(2)}`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';

    try {
      const trimmed = dateString.trim();

      // If already in YYYY-MM-DD format, parse without timezone shift
      const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (isoMatch) {
        // Use parseISO which treats date-only strings as local time
        return format(parseISO(trimmed), 'MM/dd/yyyy');
      }

      // For other formats, manually parse to avoid UTC interpretation
      // Handle "Month DD, YYYY" (full or abbreviated)
      const monthNameMatch = trimmed.match(/^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/);
      if (monthNameMatch) {
        const [, month, day, year] = monthNameMatch;
        // Create date with explicit local time (noon to avoid any DST edge cases)
        const d = new Date(parseInt(year), getMonthIndex(month), parseInt(day), 12, 0, 0);
        if (!isNaN(d.getTime())) {
          return format(d, 'MM/dd/yyyy');
        }
      }

      // Handle MM/DD/YYYY - already in display format
      const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (slashMatch) {
        const [, m, d, y] = slashMatch;
        return `${m.padStart(2, '0')}/${d.padStart(2, '0')}/${y}`;
      }

      // Fallback: use parseISO for ISO-like strings, otherwise local Date with noon time
      const parsed = parseISO(trimmed);
      if (!isNaN(parsed.getTime())) {
        return format(parsed, 'MM/dd/yyyy');
      }

      return dateString;
    } catch {
      return dateString;
    }
  };

  const getMonthIndex = (month: string): number => {
    const monthMap: { [key: string]: number } = {
      'Jan': 0, 'January': 0,
      'Feb': 1, 'February': 1,
      'Mar': 2, 'March': 2,
      'Apr': 3, 'April': 3,
      'May': 4,
      'Jun': 5, 'June': 5,
      'Jul': 6, 'July': 6,
      'Aug': 7, 'August': 7,
      'Sep': 8, 'September': 8,
      'Oct': 9, 'October': 9,
      'Nov': 10, 'November': 10,
      'Dec': 11, 'December': 11
    };
    return monthMap[month] ?? 0;
  };

  const getConfidenceColor = (score?: number) => {
    if (!score) return 'text-gray-500 dark:text-gray-400';
    if (score >= 0.8) return 'text-green-600 dark:text-green-400';
    if (score >= 0.6) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getConfidenceBadge = (score?: number) => {
    if (!score) return { text: 'Unknown', color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300' };
    if (score >= 0.8) return { text: 'High', color: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-400' };
    if (score >= 0.6) return { text: 'Medium', color: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-400' };
    return { text: 'Low', color: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-400' };
  };

  const confidenceBadge = getConfidenceBadge(data.confidence_score);

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden flex flex-col ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-600 to-brand-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-responsive-2xl font-semibold text-white">
              {data.document_type 
                ? data.document_type.charAt(0).toUpperCase() + data.document_type.slice(1) 
                : 'Document'}
            </h2>
            {filename && (
              <p className="text-brand-100 text-sm mt-1">Source: {filename}</p>
            )}
          </div>
          <div className="text-right">
            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${confidenceBadge.color}`}>
              {confidenceBadge.text} Confidence
            </span>
            {data.confidence_score && (
              <p className={`text-sm mt-1 ${getConfidenceColor(data.confidence_score)}`}>
                {(data.confidence_score * 100).toFixed(1)}%
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6 flex-1 overflow-y-auto">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="financial-card bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <IdentificationIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
              <span className="text-responsive-sm font-medium text-gray-700 dark:text-gray-300">Invoice #</span>
            </div>
            <p className="text-responsive-lg font-semibold text-gray-900 dark:text-white text-fit" title={data.invoice_number || 'N/A'}>
              {data.invoice_number || 'N/A'}
            </p>
          </div>

          {data.po_number && (
            <div className="financial-card bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <DocumentTextIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
                <span className="text-responsive-sm font-medium text-gray-700 dark:text-gray-300">PO #</span>
              </div>
              <p className="text-responsive-lg font-semibold text-gray-900 dark:text-white text-fit" title={data.po_number}>{data.po_number}</p>
            </div>
          )}

          <div className="financial-card bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <CalendarIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
              <span className="text-responsive-sm font-medium text-gray-700 dark:text-gray-300">Date</span>
            </div>
            <p className="text-responsive-lg font-semibold text-gray-900 dark:text-white text-fit" title={formatDate(data.invoice_date)}>
              {formatDate(data.invoice_date)}
            </p>
          </div>

          {data.due_date && (
            <div className="financial-card bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <CalendarIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
                <span className="text-responsive-sm font-medium text-gray-700 dark:text-gray-300">Due Date</span>
              </div>
              <p className="text-responsive-lg font-semibold text-gray-900 dark:text-white text-fit" title={formatDate(data.due_date)}>
                {formatDate(data.due_date)}
              </p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Vendor Information */}
          {data.vendor && (
            <div className="border rounded-lg p-4">
              <div className="flex items-center mb-3">
                <BuildingOfficeIcon className="h-6 w-6 text-brand-600 mr-2" />
                <h3 className="text-responsive-lg font-semibold text-gray-900 dark:text-white">Vendor Information</h3>
              </div>
              <div className="space-y-2">
                {data.vendor.name && (
                  <p className="font-medium text-gray-900">{data.vendor.name}</p>
                )}
                {data.vendor.address?.full_address && (
                  <div className="flex items-start">
                    <MapPinIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                    <p className="text-sm text-gray-600">{data.vendor.address.full_address}</p>
                  </div>
                )}
                {data.vendor.phone && (
                  <div className="flex items-center">
                    <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <p className="text-sm text-gray-600">{data.vendor.phone}</p>
                  </div>
                )}
                {data.vendor.email && (
                  <div className="flex items-center">
                    <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <p className="text-sm text-gray-600">{data.vendor.email}</p>
                  </div>
                )}
                {data.vendor.website && (
                  <p className="text-sm text-brand-600 hover:text-brand-800">
                    <a href={data.vendor.website} target="_blank" rel="noopener noreferrer">
                      {data.vendor.website}
                    </a>
                  </p>
                )}
                {data.vendor.tax_id && (
                  <p className="text-sm text-gray-600">Tax ID: {data.vendor.tax_id}</p>
                )}
              </div>
            </div>
          )}

          {/* Customer Information */}
          {data.customer && (
            <div className="border rounded-lg p-4">
              <div className="flex items-center mb-3">
                <UserIcon className="h-6 w-6 text-green-600 mr-2" />
                <h3 className="text-responsive-lg font-semibold text-gray-900 dark:text-white">Customer Information</h3>
              </div>
              <div className="space-y-2">
                {data.customer.name && (
                  <p className="font-medium text-gray-900">{data.customer.name}</p>
                )}
                {data.customer.address?.full_address && (
                  <div className="flex items-start">
                    <MapPinIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                    <p className="text-sm text-gray-600">{data.customer.address.full_address}</p>
                  </div>
                )}
                {data.customer.phone && (
                  <div className="flex items-center">
                    <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <p className="text-sm text-gray-600">{data.customer.phone}</p>
                  </div>
                )}
                {data.customer.email && (
                  <div className="flex items-center">
                    <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <p className="text-sm text-gray-600">{data.customer.email}</p>
                  </div>
                )}
                {data.customer.customer_id && (
                  <p className="text-sm text-gray-600">Customer ID: {data.customer.customer_id}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Financial Summary */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center mb-4">
            <CurrencyDollarIcon className="h-6 w-6 text-green-600 mr-2" />
            <h3 className="text-responsive-lg font-semibold text-gray-900 dark:text-white">Financial Summary</h3>
          </div>
          <div className="financial-grid grid gap-4">
            {data.subtotal !== undefined && (
              <div className="financial-card text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-responsive-sm text-gray-600 dark:text-gray-400 mb-1">Subtotal</p>
                <p className="number-text number-display font-semibold text-gray-900 dark:text-white" title={formatCurrency(data.subtotal, data.currency)}>
                  {formatCurrency(data.subtotal, data.currency)}
                </p>
              </div>
            )}
            {data.total_tax !== undefined && (
              <div className="financial-card text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-responsive-sm text-gray-600 dark:text-gray-400 mb-1">Tax</p>
                <p className="number-text number-display font-semibold text-gray-900 dark:text-white" title={formatCurrency(data.total_tax, data.currency)}>
                  {data.total_tax ? formatCurrency(data.total_tax, data.currency) : 'N/A'}
                </p>
              </div>
            )}
            {data.discount !== undefined && (
              <div className="financial-card text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-responsive-sm text-gray-600 dark:text-gray-400 mb-1">Discount</p>
                <p className="number-text number-display font-semibold text-red-600 dark:text-red-400" title={`-${formatCurrency(data.discount, data.currency)}`}>
                  -{formatCurrency(data.discount, data.currency)}
                </p>
              </div>
            )}
            <div className="financial-card text-center p-3 bg-brand-50 dark:bg-brand-900/30 rounded-lg border-2 border-brand-200 dark:border-brand-600">
              <p className="text-responsive-sm text-brand-600 dark:text-brand-400 font-medium mb-1">Total Amount</p>
              <p className="total-text number-display font-bold text-brand-900 dark:text-brand-300 text-fit" title={formatCurrency(data.total_amount, data.currency)}>
                {formatCurrency(data.total_amount, data.currency)}
              </p>
            </div>
          </div>
        </div>

        {/* Line Items */}
        {data.line_items && data.line_items.length > 0 && (
          <div className="border rounded-lg p-4">
            <h3 className="text-responsive-lg font-semibold text-gray-900 dark:text-white mb-4">
              Line Items ({data.line_items.length})
            </h3>
            <div className="overflow-x-auto custom-scrollbar">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-2 text-left text-responsive-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-4 py-2 text-right text-responsive-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Qty
                    </th>
                    <th className="px-4 py-2 text-right text-responsive-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Unit Price
                    </th>
                    <th className="px-4 py-2 text-right text-responsive-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {data.line_items.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-4 py-3 text-responsive-sm text-gray-900 dark:text-white">
                        <div 
                          className="text-fit max-w-xs truncate" 
                          title={item.description || 'N/A'}
                        >
                          {item.description || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-responsive-sm text-gray-900 dark:text-white text-right number-display">
                        {item.quantity !== undefined ? item.quantity : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-responsive-sm text-gray-900 dark:text-white text-right number-display" title={item.unit_price !== undefined ? formatCurrency(item.unit_price, data.currency) : 'N/A'}>
                        {item.unit_price !== undefined
                          ? formatCurrency(item.unit_price, data.currency)
                          : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-responsive-sm font-medium text-gray-900 dark:text-white text-right number-display" title={item.total_price !== undefined ? formatCurrency(item.total_price, data.currency) : 'N/A'}>
                        {item.total_price !== undefined
                          ? formatCurrency(item.total_price, data.currency)
                          : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payment Information */}
        {data.payment_info && (
          <div className="border rounded-lg p-4">
            <h3 className="text-responsive-lg font-semibold text-gray-900 dark:text-white mb-3">Payment Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.payment_info.method && (
                <div>
                  <p className="text-sm text-gray-600">Method</p>
                  <p className="font-medium text-gray-900">{data.payment_info.method}</p>
                </div>
              )}
              {data.payment_info.reference && (
                <div>
                  <p className="text-sm text-gray-600">Reference</p>
                  <p className="font-medium text-gray-900">{data.payment_info.reference}</p>
                </div>
              )}
              {data.payment_info.status && (
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <span
                    className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      data.payment_info.status.toLowerCase() === 'paid'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {data.payment_info.status}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        {(data.notes || data.terms) && (
          <div className="border rounded-lg p-4">
            <h3 className="text-responsive-lg font-semibold text-gray-900 dark:text-white mb-3">Additional Information</h3>
            {data.notes && (
              <div className="mb-3">
                <p className="text-sm text-gray-600">Notes</p>
                <p className="text-sm text-gray-900">{data.notes}</p>
              </div>
            )}
            {data.terms && (
              <div>
                <p className="text-sm text-gray-600">Terms</p>
                <p className="text-sm text-gray-900">{data.terms}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceDataDisplay;
